// PDM-2747: the biochemistry box of the hospitalization list holds up to five condition rows, each a lab method
// + operator + value. Rows are ANDed; rows sharing a method are evaluated by the backend against a single
// measurement, so a range is written as two rows on one method.
//
// The helpers live in their own module because the list layout and the xlsx export hook both need them and
// importing one from the other would close an import cycle.
//
// Every field of a row carries the row number in its label ("Operátor 2", "Hodnota 2"), so that a wrapped row on a
// narrow screen still reads as one condition.
function biochemistryRow<N extends 1 | 2 | 3 | 4 | 5>(n: N) {
  return {
    method: `biochemistryMethod${n}` as const,
    operator: `biochemistryOperator${n}` as const,
    value: `biochemistryValue${n}` as const,
    label: `filter.patient.biochemistry${n}` as const,
    operatorLabel: `filter.patient.biochemistryOperator${n}` as const,
    valueLabel: `filter.patient.biochemistryValue${n}` as const,
  };
}
export const BIOCHEMISTRY_ROWS = [
  biochemistryRow(1),
  biochemistryRow(2),
  biochemistryRow(3),
  biochemistryRow(4),
  biochemistryRow(5),
] as const;
export type BiochemistryRow = typeof BIOCHEMISTRY_ROWS[number];
type BiochemistryPartName = BiochemistryRow['method'] | BiochemistryRow['operator'] | BiochemistryRow['value'];
/** The slice of the hospitalization list filters these helpers read. Structural, so both callers just pass theirs. */
export type BiochemistryFilterValues = Partial<Record<BiochemistryPartName, string>>;

/**
 * What the backend's BigDecimal accepts: an optional sign, digits with an optional decimal part, an optional
 * exponent. Deliberately not `Number()`, which also swallows Infinity, 0x10 and 0b101 - all of them numbers to
 * JavaScript and all of them rejected by BigDecimal, so the row would look complete here and blow up there.
 */
const BIOCHEMISTRY_VALUE_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
/**
 * The typed value normalized for the backend: a decimal comma is what a Czech, Slovak, German or Polish user
 * types, while the API takes a plain decimal point. Anything that is not a number gives undefined, which is what
 * keeps its row out of the query.
 */
export function parseBiochemistryValue(value: string | undefined | null): string | undefined {
  const normalized = String(value ?? '')
    .trim()
    .replace(',', '.');
  return BIOCHEMISTRY_VALUE_PATTERN.test(normalized) ? normalized : undefined;
}
/**
 * A value the user typed that is not a number (`ttt`, `50 mg`). Empty is not invalid - the row is simply not finished.
 * The field itself stays as it is (no error styling, by decision); see [hasBlockingBiochemistryRow] for what such a
 * value does to the list.
 */
export function isBiochemistryValueInvalid(value: string | undefined | null): boolean {
  return Boolean(String(value ?? '').trim()) && parseBiochemistryValue(value) === undefined;
}
/**
 * A row that would filter the list if only its value were a number: method and operator chosen, value typed but
 * not numeric. Nothing sensible can be asked of the backend for it, and quietly dropping it would leave the list
 * showing hospitalizations the user has just excluded. The list is therefore emptied without a request and the
 * export is blocked until the value is corrected or the row removed (PDM-2747 review).
 */
export function hasBlockingBiochemistryRow(patients: BiochemistryFilterValues): boolean {
  return BIOCHEMISTRY_ROWS.some(
    (row) => Boolean(patients[row.method]) && Boolean(patients[row.operator]) && isBiochemistryValueInvalid(patients[row.value])
  );
}
/** A row counts only with all three parts filled in and a value that is actually a number. */
export function isBiochemistryRowComplete(patients: BiochemistryFilterValues, row: BiochemistryRow): boolean {
  return Boolean(patients[row.method]) && Boolean(patients[row.operator]) && parseBiochemistryValue(patients[row.value]) !== undefined;
}
/** Any part of the row filled in. Such a row is unfinished but the user is clearly working on it. */
export function isBiochemistryRowStarted(patients: BiochemistryFilterValues, row: BiochemistryRow): boolean {
  return Boolean(patients[row.method]) || Boolean(patients[row.operator]) || Boolean(patients[row.value]);
}
/**
 * Rows leave no gaps: everything still filled in slides up to the first free position, so clearing a row moves
 * the ones below it up instead of leaving an empty line behind. Applied after every edit of the box.
 *
 * The medication slots pack the same way, see hospitalizedPatientsMedication.ts.
 */
export function packBiochemistryRows(patients: BiochemistryFilterValues): BiochemistryFilterValues {
  const kept = BIOCHEMISTRY_ROWS.filter((row) => isBiochemistryRowStarted(patients, row)).map((row) => ({
    method: patients[row.method],
    operator: patients[row.operator],
    value: patients[row.value],
  }));
  const packed: BiochemistryFilterValues = {};
  BIOCHEMISTRY_ROWS.forEach((row, index) => {
    packed[row.method] = kept[index]?.method;
    packed[row.operator] = kept[index]?.operator;
    packed[row.value] = kept[index]?.value;
  });
  return packed;
}
/**
 * The biochemistry part of the filters after one field of one row was edited, with the rows packed up.
 *
 * Switching to a different lab method keeps the operator and the value (PDM-2747 review): the row is usually
 * corrected, not started over, and retyping the number was the complaint. The unit shown next to the method name
 * is what tells the user whether the number still makes sense.
 */
export function applyBiochemistryRowChange(
  patients: BiochemistryFilterValues,
  row: BiochemistryRow,
  part: 'method' | 'operator' | 'value',
  newValue: string | null
): BiochemistryFilterValues {
  return packBiochemistryRows({...patients, [row[part]]: newValue ?? undefined});
}
/**
 * The biochemistry part of the filters after a whole row was deleted by its remove button. The rows below it
 * move up; clearing the three fields one by one would get there too, but nothing in the form said so.
 */
export function removeBiochemistryRow(patients: BiochemistryFilterValues, row: BiochemistryRow): BiochemistryFilterValues {
  return packBiochemistryRows({...patients, [row.method]: undefined, [row.operator]: undefined, [row.value]: undefined});
}
/**
 * Which rows the form shows. The next row appears once the previous one is complete, but a row that already
 * holds something always stays visible. Editing the box packs the rows, so a gap only ever arrives from outside
 * - a bookmarked URL or a saved filter - and even then its condition must not filter the list from a form field
 * nobody can see or correct.
 */
export function showBiochemistryRows(patients: BiochemistryFilterValues): boolean[] {
  return BIOCHEMISTRY_ROWS.map(
    (row, index) =>
      index === 0 ||
      isBiochemistryRowComplete(patients, BIOCHEMISTRY_ROWS[index - 1]) ||
      BIOCHEMISTRY_ROWS.slice(index).some((later) => isBiochemistryRowStarted(patients, later))
  );
}
/**
 * Distinct lab methods among the complete rows. The occurrence selector keys off this rather than off the row
 * count: rows on one method must be satisfied by one measurement anyway, which is by definition one day.
 */
export function countBiochemistryItems(patients: BiochemistryFilterValues): number {
  return new Set(BIOCHEMISTRY_ROWS.filter((row) => isBiochemistryRowComplete(patients, row)).map((row) => patients[row.method])).size;
}
/**
 * Only complete rows reach the backend; an unfinished row simply contributes no parameters. Both the list query
 * and the xlsx export go through here so that the two can never disagree about what is being filtered.
 */
export function biochemistryParams(patients: BiochemistryFilterValues): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};
  BIOCHEMISTRY_ROWS.filter((row) => isBiochemistryRowComplete(patients, row)).forEach((row) => {
    params[row.method] = patients[row.method];
    params[row.operator] = patients[row.operator];
    params[row.value] = parseBiochemistryValue(patients[row.value]);
  });
  return params;
}
